import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../components/DashboardLayout";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { formatDate, getCurrentDate } from "../utils/dateUtils";
import { createNotification } from "../utils/notifications";

function Payments() {
  const { t } = useTranslation();
  const [payments, setPayments] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [viewingPayment, setViewingPayment] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState(""); // "income" or "outcome"
  const itemsPerPage = 30;

  const [formData, setFormData] = useState({
    type: "income", // "income" or "outcome"
    title: "",
    amount: "",
    date: getCurrentDate(),
    candidateId: "",
    description: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Fetch candidates
  const fetchCandidates = async () => {
    try {
      const candidatesSnapshot = await getDocs(collection(db, "candidates"));
      const candidatesData = candidatesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      // Sort by firstName in memory
      candidatesData.sort((a, b) => {
        const nameA = (a.firstName || "").toLowerCase();
        const nameB = (b.firstName || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setCandidates(candidatesData);
    } catch (error) {
      console.error("Error fetching candidates:", error);
      showToast(t("payments.toast.fetchCandidatesError"), "error");
      setCandidates([]); // Set empty array on error
    }
  };

  // Fetch payments
  const fetchPayments = async () => {
    try {
      const paymentsSnapshot = await getDocs(collection(db, "payments"));
      const paymentsData = paymentsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      
      // Sort by date and createdAt in memory (more reliable than Firestore orderBy)
      paymentsData.sort((a, b) => {
        // Helper function to parse date
        const parseDate = (dateStr, fallbackTimestamp) => {
          if (!dateStr) {
            return fallbackTimestamp?.toDate ? fallbackTimestamp.toDate() : new Date(fallbackTimestamp || 0);
          }
          
          // Handle DD/MM/YYYY format
          if (dateStr.includes("/")) {
            const parts = dateStr.split("/");
            if (parts.length === 3) {
              const parsed = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
              if (!isNaN(parsed.getTime())) return parsed;
            }
          }
          
          // Handle YYYY-MM-DD format or other formats
          const parsed = new Date(dateStr);
          if (!isNaN(parsed.getTime())) return parsed;
          
          // Fallback to createdAt
          return fallbackTimestamp?.toDate ? fallbackTimestamp.toDate() : new Date(fallbackTimestamp || 0);
        };
        
        const dateA = parseDate(a.date, a.createdAt);
        const dateB = parseDate(b.date, b.createdAt);
        
        if (dateB.getTime() !== dateA.getTime()) {
          return dateB.getTime() - dateA.getTime();
        }
        
        // If dates are equal, sort by createdAt
        const createdAtA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const createdAtB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return createdAtB.getTime() - createdAtA.getTime();
      });
      
      setPayments(paymentsData);
    } catch (error) {
      console.error("Error fetching payments:", error);
      showToast(t("payments.toast.fetchError"), "error");
      setPayments([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchCandidates(), fetchPayments()]);
      } catch (error) {
        console.error("Error loading data:", error);
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Show toast notification
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Calculate stats
  const stats = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisYear = new Date(now.getFullYear(), 0, 1);

    let todayIncome = 0;
    let todayOutcome = 0;
    let monthIncome = 0;
    let monthOutcome = 0;
    let yearIncome = 0;
    let yearOutcome = 0;
    let allTimeIncome = 0;
    let allTimeOutcome = 0;

    payments.forEach((payment) => {
      let paymentDate;
      if (payment.date) {
        // Handle YYYY-MM-DD format (from form) or DD/MM/YYYY format
        if (payment.date.includes("/")) {
          // DD/MM/YYYY format
          const parts = payment.date.split("/");
          if (parts.length === 3) {
            paymentDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
          } else {
            paymentDate = new Date(payment.date);
          }
        } else {
          // YYYY-MM-DD format
          paymentDate = new Date(payment.date);
        }
        // Validate date
        if (isNaN(paymentDate.getTime())) {
          paymentDate = payment.createdAt?.toDate ? payment.createdAt.toDate() : new Date(payment.createdAt || 0);
        }
      } else {
        paymentDate = payment.createdAt?.toDate ? payment.createdAt.toDate() : new Date(payment.createdAt || 0);
      }

      const amount = parseFloat(payment.amount) || 0;

      if (payment.type === "income") {
        allTimeIncome += amount;
        if (paymentDate >= thisYear) {
          yearIncome += amount;
          if (paymentDate >= thisMonth) {
            monthIncome += amount;
            if (paymentDate >= today) {
              todayIncome += amount;
            }
          }
        }
      } else {
        allTimeOutcome += amount;
        if (paymentDate >= thisYear) {
          yearOutcome += amount;
          if (paymentDate >= thisMonth) {
            monthOutcome += amount;
            if (paymentDate >= today) {
              todayOutcome += amount;
            }
          }
        }
      }
    });

    return {
      today: {
        income: todayIncome,
        outcome: todayOutcome,
        profit: todayIncome - todayOutcome,
      },
      month: {
        income: monthIncome,
        outcome: monthOutcome,
        profit: monthIncome - monthOutcome,
      },
      year: {
        income: yearIncome,
        outcome: yearOutcome,
        profit: yearIncome - yearOutcome,
      },
      allTime: {
        income: allTimeIncome,
        outcome: allTimeOutcome,
        profit: allTimeIncome - allTimeOutcome,
      },
    };
  }, [payments]);

  // Filter and search payments
  const filteredPayments = useMemo(() => {
    return payments.filter((payment) => {
      const matchesSearch =
        !searchQuery ||
        payment.description
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        payment.candidateName
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase());

      const matchesType = !filterType || payment.type === filterType;

      return matchesSearch && matchesType;
    });
  }, [payments, searchQuery, filterType]);

  // Pagination
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPayments = filteredPayments.slice(startIndex, endIndex);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterType]);

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
    // Clear candidate if type is outcome
    if (name === "type" && value === "outcome") {
      setFormData({ ...formData, [name]: value, candidateId: "" });
    }
  };

  // Handle blur
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({ ...touched, [name]: true });
  };

  // Handle close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPayment(null);
    setFormData({
      type: "income",
      title: "",
      amount: "",
      date: getCurrentDate(),
      candidateId: "",
      description: "",
    });
    setErrors({});
    setTouched({});
  };

  // Handle view
  const handleView = (payment) => {
    setViewingPayment(payment);
    setIsViewModalOpen(true);
  };

  // Handle edit
  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      type: payment.type || "income",
      title: payment.title || "",
      amount: payment.amount?.toString() || "",
      date: payment.date || getCurrentDate(),
      candidateId: payment.candidateId || "",
      description: payment.description || "",
    });
    setErrors({});
    setTouched({});
    setIsModalOpen(true);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!paymentToDelete) return;

    try {
      // If it's an income payment with a candidate, subtract amount from candidate's paidAmount
      if (paymentToDelete.type === "income" && paymentToDelete.candidateId) {
        const candidate = candidates.find((c) => c.id === paymentToDelete.candidateId);
        if (candidate) {
          const currentPaidAmount = parseFloat(candidate.paidAmount) || 0;
          const paymentAmount = parseFloat(paymentToDelete.amount) || 0;
          const newPaidAmount = Math.max(0, currentPaidAmount - paymentAmount);
          await updateDoc(doc(db, "candidates", paymentToDelete.candidateId), {
            paidAmount: newPaidAmount,
          });
        }
      }

      const paymentName = `${paymentToDelete.title || "Payment"} - ${paymentToDelete.amount}€`;
      await deleteDoc(doc(db, "payments", paymentToDelete.id));
      await createNotification(
        "payment_deleted",
        "payment",
        paymentToDelete.id,
        paymentName
      );
      showToast(t("payments.toast.deleted"), "success");
      setIsDeleteModalOpen(false);
      setPaymentToDelete(null);

      // Refresh payments and candidates
      await Promise.all([fetchPayments(), fetchCandidates()]);
    } catch (error) {
      console.error("Error deleting payment:", error);
      showToast(t("payments.toast.deleteError"), "error");
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = t("payments.errors.amountRequired");
    }

    if (!formData.date) {
      newErrors.date = t("payments.errors.dateRequired");
    }

    if (!formData.description || formData.description.trim() === "") {
      newErrors.description = t("payments.errors.descriptionRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast(t("payments.toast.fillRequired"), "error");
      return;
    }

    try {
      const candidate = formData.candidateId
        ? candidates.find((c) => c.id === formData.candidateId)
        : null;

      const paymentData = {
        type: formData.type,
        title: formData.title.trim(),
        amount: parseFloat(formData.amount),
        date: formData.date,
        candidateId: formData.candidateId || null,
        candidateName: candidate
          ? `${candidate.firstName} ${candidate.lastName}`
          : null,
        description: formData.description.trim(),
      };

      if (editingPayment) {
        // Handle candidate paidAmount update when editing
        const oldAmount = parseFloat(editingPayment.amount) || 0;
        const newAmount = paymentData.amount;
        const oldCandidateId = editingPayment.candidateId;
        const newCandidateId = paymentData.candidateId;
        const oldType = editingPayment.type;
        const newType = paymentData.type;

        // If old payment was income with candidate, subtract old amount
        if (oldType === "income" && oldCandidateId) {
          const oldCandidate = candidates.find((c) => c.id === oldCandidateId);
          if (oldCandidate) {
            const currentPaidAmount = parseFloat(oldCandidate.paidAmount) || 0;
            const newPaidAmount = Math.max(0, currentPaidAmount - oldAmount);
            await updateDoc(doc(db, "candidates", oldCandidateId), {
              paidAmount: newPaidAmount,
            });
          }
        }

        // If new payment is income with candidate, add new amount
        if (newType === "income" && newCandidateId) {
          const newCandidate = candidates.find((c) => c.id === newCandidateId);
          if (newCandidate) {
            const currentPaidAmount = parseFloat(newCandidate.paidAmount) || 0;
            const newPaidAmount = currentPaidAmount + newAmount;
            await updateDoc(doc(db, "candidates", newCandidateId), {
              paidAmount: newPaidAmount,
            });
          }
        }

        // Update existing payment
        const paymentRef = doc(db, "payments", editingPayment.id);
        await updateDoc(paymentRef, {
          ...paymentData,
          updatedAt: serverTimestamp(),
        });
        const paymentName = `${paymentData.title} - ${paymentData.amount}€`;
        await createNotification(
          "payment_updated",
          "payment",
          editingPayment.id,
          paymentName
        );
        showToast(t("payments.toast.updated"), "success");
      } else {
        // Create new payment
        const docRef = await addDoc(collection(db, "payments"), {
          ...paymentData,
          createdAt: serverTimestamp(),
        });

        // If it's an income payment with a candidate, update candidate's paidAmount
        if (paymentData.type === "income" && paymentData.candidateId && candidate) {
          const currentPaidAmount = parseFloat(candidate.paidAmount) || 0;
          const newPaidAmount = currentPaidAmount + paymentData.amount;
          await updateDoc(doc(db, "candidates", paymentData.candidateId), {
            paidAmount: newPaidAmount,
          });
        }

        const paymentName = `${paymentData.title} - ${paymentData.amount}€`;
        await createNotification(
          "payment_added",
          "payment",
          docRef.id,
          paymentName
        );
        showToast(t("payments.toast.paymentAdded"), "success");
      }

      handleCloseModal();

      // Refresh payments and candidates
      await Promise.all([fetchPayments(), fetchCandidates()]);
    } catch (error) {
      console.error("Error saving payment:", error);
      showToast(t("payments.toast.saveError"), "error");
    }
  };

  // Get candidate name by ID
  const getCandidateName = (candidateId) => {
    if (!candidateId) return null;
    const candidate = candidates.find((c) => c.id === candidateId);
    return candidate ? `${candidate.firstName} ${candidate.lastName}` : null;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Toast Notification */}
        {toast.show && (
          <div
            className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${
              toast.type === "success"
                ? "bg-green-500 text-white"
                : "bg-red-500 text-white"
            }`}
          >
            {toast.message}
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t("payments.title")}
            </h2>
            <p className="text-gray-600">{t("payments.subtitle")}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium flex items-center space-x-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              <span>{t("payments.addPayment")}</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Today */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {t("payments.stats.today")}
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {t("payments.stats.income")}
                </span>
                <span className="font-semibold text-green-600">
                  €{Math.round(stats.today.income)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {t("payments.stats.outcome")}
                </span>
                <span className="font-semibold text-red-600">
                  €{Math.round(stats.today.outcome)}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="text-sm font-medium text-gray-900">
                  {t("payments.stats.profit")}
                </span>
                <span
                  className={`font-bold ${
                    stats.today.profit >= 0 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  €{Math.round(stats.today.profit)}
                </span>
              </div>
            </div>
          </div>

          {/* This Month */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {t("payments.stats.thisMonth")}
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {t("payments.stats.income")}
                </span>
                <span className="font-semibold text-green-600">
                  €{Math.round(stats.month.income)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {t("payments.stats.outcome")}
                </span>
                <span className="font-semibold text-red-600">
                  €{Math.round(stats.month.outcome)}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="text-sm font-medium text-gray-900">
                  {t("payments.stats.profit")}
                </span>
                <span
                  className={`font-bold ${
                    stats.month.profit >= 0 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  €{Math.round(stats.month.profit)}
                </span>
              </div>
            </div>
          </div>

          {/* This Year */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {t("payments.stats.thisYear")}
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {t("payments.stats.income")}
                </span>
                <span className="font-semibold text-green-600">
                  €{Math.round(stats.year.income)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {t("payments.stats.outcome")}
                </span>
                <span className="font-semibold text-red-600">
                  €{Math.round(stats.year.outcome)}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="text-sm font-medium text-gray-900">
                  {t("payments.stats.profit")}
                </span>
                <span
                  className={`font-bold ${
                    stats.year.profit >= 0 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  €{Math.round(stats.year.profit)}
                </span>
              </div>
            </div>
          </div>

          {/* All Time */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-4">
              {t("payments.stats.allTime")}
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {t("payments.stats.income")}
                </span>
                <span className="font-semibold text-green-600">
                  €{Math.round(stats.allTime.income)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">
                  {t("payments.stats.outcome")}
                </span>
                <span className="font-semibold text-red-600">
                  €{Math.round(stats.allTime.outcome)}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="text-sm font-medium text-gray-900">
                  {t("payments.stats.profit")}
                </span>
                <span
                  className={`font-bold ${
                    stats.allTime.profit >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  €{Math.round(stats.allTime.profit)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search Bar */}
            <div className="relative flex-1">
              <input
                type="text"
                placeholder={t("payments.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 pl-10 border-2 border-gray-400 rounded-lg bg-gray-50 font-semibold text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-black/25 focus:border-black text-base"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>

            {/* Type Filter */}
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black text-sm bg-white"
            >
              <option value="">{t("payments.filters.allTypes")}</option>
              <option value="income">{t("payments.filters.income")}</option>
              <option value="outcome">{t("payments.filters.outcome")}</option>
            </select>

            {/* Clear Filters */}
            {(searchQuery || filterType) && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setFilterType("");
                }}
                className="px-3 py-2.5 text-sm text-gray-600 hover:text-black hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-1"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                <span>{t("payments.clearAll")}</span>
              </button>
            )}
          </div>
        </div>

        {/* Results Count */}
        {!loading && payments.length > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-600 px-2">
            <span>
              {t("payments.showing")}{" "}
              <span className="font-semibold text-gray-900">
                {filteredPayments.length > 0
                  ? `${startIndex + 1}-${Math.min(
                      endIndex,
                      filteredPayments.length
                    )}`
                  : 0}
              </span>{" "}
              {t("payments.of")}{" "}
              <span className="font-semibold text-gray-900">
                {filteredPayments.length}
              </span>{" "}
              {t("payments.paymentsText")}
              {filteredPayments.length !== payments.length && (
                <span className="text-gray-500">
                  {" "}
                  ({t("payments.of")} {payments.length} {t("payments.total")})
                </span>
              )}
            </span>
          </div>
        )}

        {/* Payments Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {filteredPayments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <svg
                className="w-16 h-16 mb-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
              <p>{t("payments.table.noPayments")}</p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="mt-4 text-black hover:underline"
              >
                {t("payments.addFirstPayment")}
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("payments.table.type")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("payments.table.title")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("payments.table.amount")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("payments.table.candidate")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("payments.table.description")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("payments.table.date")}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t("payments.table.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedPayments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                            payment.type === "income"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {t(`payments.table.${payment.type}`)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {payment.title || "-"}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm font-semibold ${
                          payment.type === "income"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {payment.type === "income" ? "+" : "-"}€
                        {Math.round(parseFloat(payment.amount || 0))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.candidateName ||
                          getCandidateName(payment.candidateId) ||
                          "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {payment.description || "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(payment.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleView(payment)}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title={t("payments.actions.view")}
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleEdit(payment)}
                            className="text-indigo-600 hover:text-indigo-900 transition-colors"
                            title={t("payments.actions.edit")}
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              setPaymentToDelete(payment);
                              setIsDeleteModalOpen(true);
                            }}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title={t("payments.actions.delete")}
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && filteredPayments.length > itemsPerPage && (
          <div className="flex items-center justify-center space-x-2 mt-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t("common.previous")}
            </button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? "bg-black text-white border-black"
                        : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t("common.next")}
            </button>
          </div>
        )}

        {/* Add Payment Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
              <div className="p-6 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-xl font-bold text-gray-900">
                  {t("payments.form.title")}
                </h3>
              </div>

              <form
                onSubmit={handleSubmit}
                className="flex flex-col flex-1 overflow-hidden"
              >
                <div className="p-6 overflow-y-auto flex-1">
                  <div className="space-y-6">
                    {/* Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("payments.form.type")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="type"
                        value={formData.type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                          touched.type && errors.type
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      >
                        <option value="income">
                          {t("payments.form.income")}
                        </option>
                        <option value="outcome">
                          {t("payments.form.outcome")}
                        </option>
                      </select>
                      {touched.type && errors.type && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.type}
                        </p>
                      )}
                    </div>

                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("payments.form.paymentTitle")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t("payments.form.titlePlaceholder")}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                          touched.title && errors.title
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      />
                      {touched.title && errors.title && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.title}
                        </p>
                      )}
                    </div>

                    {/* Amount */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("payments.form.amount")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        step="0.01"
                        min="0"
                        placeholder={t("payments.form.amountPlaceholder")}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                          touched.amount && errors.amount
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      />
                      {touched.amount && errors.amount && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.amount}
                        </p>
                      )}
                    </div>

                    {/* Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("payments.form.date")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                          touched.date && errors.date
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      />
                      {touched.date && errors.date && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.date}
                        </p>
                      )}
                    </div>

                    {/* Candidate (only for income) */}
                    {formData.type === "income" && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {t("payments.form.candidate")}{" "}
                          <span className="text-gray-500 text-xs">
                            ({t("payments.form.optional")})
                          </span>
                        </label>
                        <select
                          name="candidateId"
                          value={formData.candidateId}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10"
                        >
                          <option value="">
                            {t("payments.form.selectCandidatePlaceholder")}
                          </option>
                          {candidates.map((candidate) => (
                            <option key={candidate.id} value={candidate.id}>
                              {candidate.firstName} {candidate.lastName} (
                              {candidate.candidateId})
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          {t("payments.form.candidateHelper")}
                        </p>
                      </div>
                    )}

                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("payments.form.description")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        rows={4}
                        placeholder={t("payments.form.descriptionPlaceholder")}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 ${
                          touched.description && errors.description
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                      />
                      {touched.description && errors.description && (
                        <p className="mt-1 text-xs text-red-500">
                          {errors.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="p-6 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
                  >
                    {editingPayment
                      ? t("payments.form.updatePayment")
                      : t("payments.form.addPayment")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View Payment Modal */}
        {isViewModalOpen && viewingPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
                <h3 className="text-xl font-bold text-gray-900">
                  {t("payments.view.title")}
                </h3>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t("payments.table.type")}
                      </label>
                      <span
                        className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${
                          viewingPayment.type === "income"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {t(`payments.table.${viewingPayment.type}`)}
                      </span>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t("payments.table.amount")}
                      </label>
                      <p
                        className={`text-lg font-semibold ${
                          viewingPayment.type === "income"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {viewingPayment.type === "income" ? "+" : "-"}€
                        {Math.round(parseFloat(viewingPayment.amount || 0))}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t("payments.table.date")}
                      </label>
                      <p className="text-sm text-gray-900">
                        {formatDate(viewingPayment.date)}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">
                        {t("payments.table.candidate")}
                      </label>
                      <p className="text-sm text-gray-900">
                        {viewingPayment.candidateName ||
                          getCandidateName(viewingPayment.candidateId) ||
                          "-"}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      {t("payments.table.title")}
                    </label>
                    <p className="text-sm text-gray-900">
                      {viewingPayment.title || "-"}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      {t("payments.table.description")}
                    </label>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">
                      {viewingPayment.description || "-"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end flex-shrink-0">
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
                >
                  {t("common.close")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && paymentToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  {t("payments.delete.title")}
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {t("payments.delete.message", {
                    amount: `€${Math.round(parseFloat(paymentToDelete.amount || 0))}`,
                    description: paymentToDelete.description || "",
                  })}
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setIsDeleteModalOpen(false);
                      setPaymentToDelete(null);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                  >
                    {t("payments.delete.confirm")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Payments;
